import React from "react";

/**
 * RouteSpecs — pannello "Specifiche caccia al tesoro" renderizzato
 * nella scheda percorso (RouteDetail). Mostra 4 rating 0-5 + età minima.
 *
 * Props:
 *   specs: {
 *     contesto_urbano: 0..5,
 *     sfida_mentale:   0..5,
 *     movimento:       0..5,
 *     spirito_squadra: 0..5,
 *     min_age:         number|null
 *   }
 */
const ROWS = [
  { key: "contesto_urbano", label: "Contesto urbano",   hint: "Ambiente di gioco" },
  { key: "sfida_mentale",   label: "Sfida mentale",     hint: "Quanto fa pensare" },
  { key: "movimento",       label: "Movimento",         hint: "Componente fisica" },
  { key: "spirito_squadra", label: "Spirito di squadra", hint: "Adatta al gruppo" },
];

export default function RouteSpecs({ specs }) {
  if (!specs) return null;
  // Considera "vuoto" se tutti i rating sono 0 e min_age non è settato
  const allZero =
    !specs.contesto_urbano && !specs.sfida_mentale && !specs.movimento &&
    !specs.spirito_squadra && (specs.min_age == null);
  if (allZero) return null;

  return (
    <div className="rounded-3xl border-2 border-outline bg-parchment-dark/60 p-4" data-testid="route-specs">
      <h3 className="font-display font-extrabold text-lg text-ink-900 mb-3">Specifiche caccia al tesoro</h3>
      <div className="space-y-2.5">
        {ROWS.map((r) => (
          <SpecRow
            key={r.key}
            label={r.label}
            value={specs[r.key] || 0}
            testId={`spec-${r.key}`}
          />
        ))}
        {specs.min_age != null && (
          <div
            className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-outline"
            data-testid="spec-min-age"
          >
            <div className="min-w-0">
              <div className="font-bold text-ink-800 text-sm leading-tight">Età minima consigliata</div>
              <div className="text-[11px] text-ink-500">Adatta a partire da questa età</div>
            </div>
            <div className="shrink-0 px-3 py-1 rounded-full bg-forest text-white font-display font-extrabold text-sm">
              {specs.min_age}+ anni
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SpecRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={testId}>
      <div className="font-bold text-ink-800 text-sm">{label}</div>
      <Dots value={value}/>
    </div>
  );
}

function Dots({ value, total = 5 }) {
  const safe = Math.max(0, Math.min(total, Number(value) || 0));
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={`dot-view-${i}`}
          className={`w-3.5 h-3.5 rounded-full border ${
            i < safe ? "bg-gold border-gold-hover" : "bg-parchment border-outline"
          }`}
          aria-label={i < safe ? "pieno" : "vuoto"}
        />
      ))}
    </div>
  );
}

/** Picker editor 0-5 (per Admin form). Click su un pallino imposta il valore. */
export function DotRatingPicker({ value = 0, onChange, total = 5, testId = "dot-rating" }) {
  const safe = Math.max(0, Math.min(total, Number(value) || 0));
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <button
            key={`dot-picker-${i}`}
            type="button"
            onClick={() => onChange(i + 1 === safe ? 0 : i + 1)}
            className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
              i < safe ? "bg-gold border-gold-hover" : "bg-parchment border-outline"
            }`}
            title={`${i + 1}/${total}`}
            data-testid={`${testId}-${i + 1}`}
          />
        ))}
      </div>
      <span className="text-xs text-ink-500 font-bold tabular-nums w-8">{safe}/{total}</span>
    </div>
  );
}
